// Training plan of the week: two skills picked from his age and what's not
// yet reliable, practised in three 5-minute sessions a day. Pure.
//
// The week's pick is saved (collection `trainingWeeks`, id = the Monday) so
// it stays put all week even as levels change; it's made the first time a
// device needs it, and the pick is deterministic so two devices agree.

import { SKILLS } from '../content/training.ts';
import type { Skill } from '../content/types.ts';
import { addDays, parseISODate, today } from './time.ts';
import type { ISODate, LogEvent, Millis, SkillProgress, TrainingWeek } from './types.ts';

export const SESSIONS_PER_DAY = 3;
export const SESSION_MINUTES = 5;
/** How long a session should be at his age: about 3 minutes under 12 weeks (a young puppy tips into nipping fast), then 5. */
export const sessionMinutes = (weeks: number | undefined) => (weeks !== undefined && weeks < 12 ? 3 : SESSION_MINUTES);

/** The Monday of the week containing `now` (local time). */
export function weekStart(now: Millis): ISODate {
  const d = today(now);
  const dow = (parseISODate(d).getDay() + 6) % 7; // Mon = 0
  return addDays(d, -dow);
}

/** Skills worth working on now, best first: in-progress ones, then the curriculum in order. */
export function candidateSkills(weeks: number, progress: SkillProgress[]): Skill[] {
  const level = new Map(progress.map((p) => [p.id, p.level]));
  return SKILLS.map((s, order) => ({ s, order, lv: level.get(s.id) ?? 0 }))
    .filter(({ s, lv }) => s.fromWeeks <= weeks + 1 && lv < 3)
    .sort((a, b) => Number(b.lv > 0) - Number(a.lv > 0) || a.s.stage - b.s.stage || a.order - b.order)
    .map(({ s }) => s);
}

/** The two skills for a week. */
export function pickWeek(weeks: number, progress: SkillProgress[]): string[] {
  return candidateSkills(weeks, progress)
    .slice(0, 2)
    .map((s) => s.id);
}

/** Swap one skill for the next candidate not already in the plan. */
export function swapSkill(plan: TrainingWeek, out: string, weeks: number, progress: SkillProgress[]): TrainingWeek {
  const next = candidateSkills(weeks, progress).find((s) => !plan.skills.includes(s.id));
  if (!next) return plan;
  return { ...plan, skills: plan.skills.map((id) => (id === out ? next.id : id)) };
}

export interface WeekProgress {
  /** Sessions per day, Monday first. */
  days: number[];
  /** Today's sessions. */
  today: number;
  total: number;
  /** Index of today in `days` (0 = Monday). */
  todayIndex: number;
}

/** Training sessions logged this week that included one of the week's skills. */
export function weekProgress(plan: TrainingWeek, events: LogEvent[], now: Millis): WeekProgress {
  const start = parseISODate(plan.id).getTime();
  const days = Array.from({ length: 7 }, () => 0);
  for (const e of events) {
    if (e.type !== 'training' || !e.data.skills.some((k) => plan.skills.includes(k))) continue;
    const d = today(e.at);
    const i = Math.round((parseISODate(d).getTime() - start) / 86_400_000);
    if (i >= 0 && i < 7) days[i]++;
  }
  const todayIndex = Math.max(0, Math.min(6, Math.round((parseISODate(today(now)).getTime() - start) / 86_400_000)));
  return { days, today: days[todayIndex], total: days.reduce((a, b) => a + b, 0), todayIndex };
}

export const skillName = (id: string) => SKILLS.find((s) => s.id === id)?.name ?? id;
export const planTitle = (plan: TrainingWeek) => plan.skills.map(skillName).join(' · ');
